import { useEffect, useState } from 'react';

// mui
import { Avatar, Box, Grid, Skeleton, Typography } from '@mui/material';
import { VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';

// client-shared
import { storage } from '@goldblack/client-storage';
import { avatar } from '@goldblack/client-utils';
import { def } from '@goldblack/shared';

import { DotLoading } from '../animation/dot-loading.jsx';

// Below this size a text label does not fit in the circle, so only an icon is shown.
const MIN_LABEL_SIZE = 50;

// Status overlay for an image that is still being moderated (PROCESSING) or was flagged
// (CENSORED): same treatment as a post — blurred image under a dimmed layer with a label.
function ImageStatusOverlay({ size, censored }) {
    const showLabel = size >= MIN_LABEL_SIZE;
    return (
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignItems='center'
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: size,
                height: size,
                borderRadius: '50%',
                // PROCESSING: no dark layer, the label sits on the skeleton drawn underneath.
                backgroundColor: censored ? 'rgba(0,0,0,0.6)' : 'transparent',
                pointerEvents: 'none',
            }}>
            {censored ? (
                showLabel ? (
                    <Typography
                        sx={{
                            color: 'white',
                            fontWeight: 'normal',
                            fontFamily: 'montserrat',
                            fontSize: '10px',
                            lineHeight: 1.1,
                            textAlign: 'center',
                        }}>
                        Censored
                    </Typography>
                ) : (
                    <VisibilityOffIcon sx={{ color: 'white', fontSize: Math.round(size * 0.5) }} />
                )
            ) : (
                <DotLoading size={Math.max(3, Math.round(size / 12))} />
            )}
        </Grid>
    );
}

// `loading` forces the skeleton while the parent is still fetching the user (image
// URL unknown). Once the URL is known, this component tracks the image download itself.
// `own` forces the owner check for user objects that carry no _id (e.g. the profile header).
function UserAvatar({ loading, variant, user, config, onClick, own, sx }) {
    let avatarSize = 38;

    switch (variant) {
        case 'post':
        case 'comment':
            avatarSize = config.postAvatarSize; // 38px
            break;
        case 'search':
        case 'trending':
        case 'chatlist':
        case 'follower':
        case 'following':
        case 'chatroom':
            avatarSize = config.userAvatarSize; // 50px
            break;
        case 'profile':
            avatarSize = config.profileAvatarSize; // 77px
            break;
        default:
            avatarSize = 38;
            break;
    }

    // Use thumbnail if available, fall back to image
    const imageUrl = user?.thumbnail || user?.image;
    // Image moderation state (def.PostStatus): shown to every viewer, like a post's status.
    const censored = user?.imageStatus === def.PostStatus.CENSORED;
    const flagged = Boolean(imageUrl) && (censored || user?.imageStatus === def.PostStatus.PROCESSING);
    // Like a post's media: while PROCESSING the image may not be in R2 yet, and an early request
    // gets a 404 the CDN caches (then the ML box's download of the same URL fails too). So don't
    // request it — show only the dark placeholder. Local blob: previews are fine.
    const pending = user?.imageStatus === def.PostStatus.PROCESSING && !imageUrl?.startsWith('blob:');
    // The processing indicator is for the owner only.
    const self = storage.transience.getValue('userSession');
    const isOwn = own ?? (Boolean(self?._id && user?._id) && String(self._id) === String(user._id));
    // Owner: empty circle with border + dots. Others: the plain letter avatar.
    const showProgress = pending && isOwn;
    const [imgLoaded, setImgLoaded] = useState(false);
    // Re-arm the skeleton when the image URL changes (e.g. virtualized row reuse).
    useEffect(() => {
        setImgLoaded(false);
    }, [imageUrl]);

    // Skeleton while the parent is still fetching (loading), or while a known image
    // URL is still downloading. No image URL → letter avatar, no skeleton.
    const showSkeleton = loading || (Boolean(imageUrl) && !pending && !imgLoaded);

    return (
        <Grid
            container
            direction='column'
            justifyContent='center'
            alignContent='center'
            sx={{
                position: 'relative',
                width: avatarSize,
                height: avatarSize,
                overflow: 'visible',
                flexShrink: 0,
                ...sx,
            }}>
            {/* Avatar is always mounted so the image loads; the skeleton overlays it until
                then. onLoad/onError go on the <img> via slotProps.img — MUI spreads
                top-level props onto the root <span>, where they'd never fire on load. */}
            <Avatar
                alt={user?.username}
                src={pending ? undefined : imageUrl}
                sx={{
                    width: avatarSize,
                    height: avatarSize,
                    // Hide the gold (primary.main) fallback bg while the skeleton is up —
                    // the skeleton is a translucent overlay, so a solid bg behind it shows
                    // through and makes the skeleton indistinguishable from the gold circle.
                    bgcolor: showSkeleton || showProgress ? 'transparent' : avatar.stringToColor(user?.username),
                    fontSize: '24px',
                }}
                slotProps={{
                    img: {
                        onLoad: () => setImgLoaded(true),
                        onError: () => setImgLoaded(true),
                        // Same blur as a post's media (blur(20px) @ full size), scaled to the circle;
                        // the slight zoom hides the transparent fringe blur leaves at the edge.
                        style: flagged
                            ? { filter: `blur(${Math.round(avatarSize * 0.25)}px)`, transform: 'scale(1.15)' }
                            : undefined,
                    },
                }}
                onClick={onClick}>
                {showProgress ? null : avatar.stringToDisplay(user?.username)}
            </Avatar>
            {/* PROCESSING: no skeleton, just a circular border for the slot (owner only). */}
            {showProgress && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: avatarSize,
                        height: avatarSize,
                        borderRadius: '50%',
                        border: 1,
                        borderColor: 'primary.main',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                    }}
                />
            )}
            {showSkeleton && (
                <Skeleton
                    variant='circular'
                    width={avatarSize}
                    height={avatarSize}
                    sx={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                />
            )}
            {flagged && !showSkeleton && (censored || isOwn) && (
                <ImageStatusOverlay size={avatarSize} censored={censored} />
            )}
        </Grid>
    );
}

export { UserAvatar };
